import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { showToast } from "../components/Toast";

const BASE_URL = "https://api.shubhamraj01.in";

export default function CheckoutPage({ setActivePage }) {
  const { token } = useAuth();
  const { cart, cartSubtotal, clearCart } = useCart();

  const [step, setStep] = useState("address"); // "address" | "payment"
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [error, setError] = useState("");

  const [address, setAddress] = useState({
    street: "", city: "", state: "", pincode: "", country: "India",
  });

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const restaurantId = cart[0]?.restaurantId;

  const handleAddressChange = (e) => {
    setAddress(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  // Step 1 — Place order with address
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!address.street || !address.city || !address.pincode) {
      setError("Street, city aur pincode required hai!");
      return;
    }
    if (cart.length === 0) { setError("Cart khali hai!"); return; }

    setLoading(true);
    setError("");
    try {
      // Add items to backend cart
      for (const item of cart) {
        await fetch(`${BASE_URL}/api/cart-item/add`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ foodId: item.id, quantity: item.qty, ingredients: [] }),
        });
      }

      // Place order
      const res = await fetch(`${BASE_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          restaurantId,
          deliveryAddress: address,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Order place nahi hua");
      }

      const order = await res.json();
      setOrderId(order.id);
      setStep("payment");
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — Razorpay payment
  const handleRazorpay = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/payment/razorpay/${orderId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      // Load Razorpay script
      if (!window.Razorpay) {
        await new Promise((resolve) => {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "Savora",
        description: `Order #${orderId}`,
        order_id: data.razorpayOrderId,
        handler: async (response) => {
          try {
            await fetch(`${BASE_URL}/api/payment/razorpay/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });
            clearCart();
            showToast("Payment successful! Order confirmed!");
            setActivePage("orders");
          } catch(e) {
            setError("Payment verify nahi hua: " + e.message);
          }
        },
        theme: { color: "#e85d2f" },
        modal: { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — COD
  const handleCOD = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/payment/cod/${orderId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("COD failed");
      clearCart();
      showToast("Order confirmed! Cash on delivery.");
      setActivePage("orders");
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderId) {
    return (
      <div style={{ textAlign: "center", padding: "6rem 2rem", color: "var(--text2)" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{"🛒"}</div>
        <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800 }}>Cart khali hai!</h2>
        <button onClick={() => setActivePage("restaurants")} style={{
          marginTop: "1.5rem", background: "var(--primary)", color: "#fff",
          border: "none", padding: ".65rem 1.5rem", borderRadius: "99px",
          fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
        }}>Restaurants dekhein</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem clamp(1rem,5vw,3rem)" }}>

      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <button onClick={() => setActivePage("restaurants")} style={{
          background: "none", border: "none", color: "var(--text2)",
          cursor: "pointer", fontSize: ".9rem", marginBottom: "1rem",
          padding: 0, fontFamily: "inherit",
        }}>
          Back
        </button>
        <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1.8rem", letterSpacing: "-0.03em" }}>
          Checkout
        </h1>

        {/* Steps */}
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginTop: "1rem" }}>
          {["address", "payment"].map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "50%",
                background: step === s || (s === "address" && step === "payment") ? "var(--primary)" : "var(--border)",
                color: step === s || (s === "address" && step === "payment") ? "#fff" : "var(--text3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: ".8rem", fontWeight: 700,
              }}>{i + 1}</div>
              <span style={{
                fontSize: ".85rem", fontWeight: 600,
                color: step === s ? "var(--text)" : "var(--text3)",
                textTransform: "capitalize",
              }}>{s === "address" ? "Delivery Address" : "Payment"}</span>
              {i === 0 && <div style={{ width: "40px", height: "1px", background: "var(--border)" }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "2rem", alignItems: "start" }}>

        {/* Left — Form */}
        <div>
          {/* Step 1 — Address */}
          {step === "address" && (
            <div style={{
              background: "var(--surface)", border: "1.5px solid var(--border)",
              borderRadius: "20px", padding: "1.75rem",
            }}>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1.1rem", marginBottom: "1.5rem" }}>
                Delivery Address
              </h2>

              <form onSubmit={handlePlaceOrder} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                  <label style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: ".4rem" }}>
                    Street *
                  </label>
                  <input type="text" name="street" value={address.street} onChange={handleAddressChange}
                    placeholder="123 Main Street, Apartment 4B"
                    style={{
                      width: "100%", padding: ".65rem 1rem", borderRadius: "10px",
                      border: "1.5px solid var(--border)", background: "var(--bg)",
                      color: "var(--text)", fontSize: ".9rem", outline: "none", fontFamily: "inherit",
                    }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div>
                    <label style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: ".4rem" }}>
                      City *
                    </label>
                    <input type="text" name="city" value={address.city} onChange={handleAddressChange}
                      placeholder="Mumbai"
                      style={{
                        width: "100%", padding: ".65rem 1rem", borderRadius: "10px",
                        border: "1.5px solid var(--border)", background: "var(--bg)",
                        color: "var(--text)", fontSize: ".9rem", outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: ".4rem" }}>
                      State
                    </label>
                    <input type="text" name="state" value={address.state} onChange={handleAddressChange}
                      placeholder="Maharashtra"
                      style={{
                        width: "100%", padding: ".65rem 1rem", borderRadius: "10px",
                        border: "1.5px solid var(--border)", background: "var(--bg)",
                        color: "var(--text)", fontSize: ".9rem", outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div>
                    <label style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: ".4rem" }}>
                      Pincode *
                    </label>
                    <input type="text" name="pincode" value={address.pincode} onChange={handleAddressChange}
                      placeholder="400001"
                      style={{
                        width: "100%", padding: ".65rem 1rem", borderRadius: "10px",
                        border: "1.5px solid var(--border)", background: "var(--bg)",
                        color: "var(--text)", fontSize: ".9rem", outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: ".4rem" }}>
                      Country
                    </label>
                    <input type="text" name="country" value={address.country} onChange={handleAddressChange}
                      style={{
                        width: "100%", padding: ".65rem 1rem", borderRadius: "10px",
                        border: "1.5px solid var(--border)", background: "var(--bg)",
                        color: "var(--text)", fontSize: ".9rem", outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </div>
                </div>

                {error && (
                  <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: "10px", padding: ".65rem 1rem", fontSize: ".85rem" }}>
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} style={{
                  width: "100%", background: "var(--primary)", color: "#fff", border: "none",
                  padding: ".85rem", borderRadius: "12px", fontWeight: 700, fontSize: ".95rem",
                  cursor: loading ? "not-allowed" : "pointer", opacity: loading ? .6 : 1,
                  fontFamily: "inherit", marginTop: ".5rem",
                }}>
                  {loading ? "Placing order..." : "Continue to Payment"}
                </button>
              </form>
            </div>
          )}

          {/* Step 2 — Payment */}
          {step === "payment" && (
            <div style={{
              background: "var(--surface)", border: "1.5px solid var(--border)",
              borderRadius: "20px", padding: "1.75rem",
            }}>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1.1rem", marginBottom: ".5rem" }}>
                Payment Method
              </h2>
              <p style={{ color: "var(--text2)", fontSize: ".85rem", marginBottom: "1.5rem" }}>
                Order #{orderId} placed! Ab payment karo.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                {/* Razorpay */}
                <button onClick={handleRazorpay} disabled={loading} style={{
                  background: "var(--bg)", border: "1.5px solid var(--border)",
                  borderRadius: "16px", padding: "1.1rem 1.25rem",
                  display: "flex", alignItems: "center", gap: "1rem",
                  cursor: "pointer", transition: "all .2s", width: "100%", textAlign: "left",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--primary)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                >
                  <div style={{
                    width: "44px", height: "44px", background: "#e8f4fd", borderRadius: "12px",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0,
                  }}>{"💳"}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: ".9rem", color: "var(--text)" }}>Pay Online</div>
                    <div style={{ fontSize: ".78rem", color: "var(--text3)", marginTop: ".15rem" }}>UPI · Cards · Net Banking · Wallets</div>
                  </div>
                  <div style={{ marginLeft: "auto", color: "var(--primary)", fontWeight: 700 }}>→</div>
                </button>

                {/* COD */}
                <button onClick={handleCOD} disabled={loading} style={{
                  background: "var(--bg)", border: "1.5px solid var(--border)",
                  borderRadius: "16px", padding: "1.1rem 1.25rem",
                  display: "flex", alignItems: "center", gap: "1rem",
                  cursor: "pointer", transition: "all .2s", width: "100%", textAlign: "left",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                >
                  <div style={{
                    width: "44px", height: "44px", background: "var(--accent-l)", borderRadius: "12px",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0,
                  }}>{"💵"}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: ".9rem", color: "var(--text)" }}>Cash on Delivery</div>
                    <div style={{ fontSize: ".78rem", color: "var(--text3)", marginTop: ".15rem" }}>Delivery pe cash dena hoga</div>
                  </div>
                  <div style={{ marginLeft: "auto", color: "var(--accent)", fontWeight: 700 }}>→</div>
                </button>
              </div>

              {error && (
                <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: "10px", padding: ".65rem 1rem", fontSize: ".85rem", marginTop: "1rem" }}>
                  {error}
                </div>
              )}

              {loading && (
                <div style={{ textAlign: "center", marginTop: "1rem", color: "var(--text2)", fontSize: ".85rem" }}>
                  Processing...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right — Order Summary */}
        <div style={{
          background: "var(--surface)", border: "1.5px solid var(--border)",
          borderRadius: "20px", padding: "1.5rem",
          position: "sticky", top: "calc(68px + 1rem)",
        }}>
          <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1rem", marginBottom: "1.25rem" }}>
            Order Summary
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", marginBottom: "1rem" }}>
            {cart.map(item => (
              <div key={item.id} style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem" }}>
                <span style={{ color: "var(--text2)" }}>{item.name} x{item.qty}</span>
                <span style={{ fontWeight: 600 }}>Rs.{item.price * item.qty}</span>
              </div>
            ))}
          </div>
          <div style={{ height: "1px", background: "var(--border)", margin: ".75rem 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem", color: "var(--text2)", marginBottom: ".4rem" }}>
            <span>Subtotal</span><span>Rs.{cartSubtotal}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem", color: "var(--text2)", marginBottom: ".75rem" }}>
            <span>Delivery</span><span>Rs.40</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: "1.05rem" }}>
            <span>Total</span><span style={{ color: "var(--primary)" }}>Rs.{cartSubtotal + 40}</span>
          </div>
        </div>
      </div>
    </div>
  );
}